import { useDispatch, useSelector } from 'react-redux';
import lodash from 'lodash';
import { useApolloClient } from 'react-apollo';
import gql from 'graphql-tag';

import { IStoreState } from '../../store/types';
import { useEffect, useMemo } from 'react';
import {
  ContentStackState,
  SetDataAction,
  NewContentstackState
} from '../../store/reducers/content-stack/content-stack.reducer';
import { getLogger } from '../logger-plus';

/** requires a single call of the named export hook useInitialize in the component hierarchy (e.g. the app) */
const useContentStack = () => useSelector(({ contentstack }: ReturnType<IStoreState>) => contentstack.data);

export const useHomePageContent = ({ merchandisedHomePage }: { merchandisedHomePage: boolean }) => {
  const contentStack = useContentStack();

  return merchandisedHomePage
    ? {
        home_page_image: contentStack?.homepage_merchandising?.image?.url,
        home_page_rg_image: contentStack?.home_page?.rg_image?.url,
        home_page_temporary_image: contentStack?.homepage_merchandising?.temporary_image?.url,
        home_page_temporary_cta_link: contentStack?.homepage_merchandising?.temporary_cta_link,
        home_page_temporary_image_start: contentStack?.homepage_merchandising?.temporary_image_start,
        home_page_temporary_image_expiry: contentStack?.homepage_merchandising?.temporary_image_expiry
      }
    : {
        home_page_image: contentStack?.home_page?.image?.url,
        home_page_rg_image: contentStack?.home_page?.rg_image?.url,
        home_page_temporary_image: contentStack?.home_page?.temporary_image?.url,
        home_page_temporary_cta_link: contentStack?.home_page?.temporary_cta_link,
        home_page_temporary_image_start: contentStack?.home_page?.temporary_image_start,
        home_page_temporary_image_expiry: contentStack?.home_page?.temporary_image_expiry
      };
};

export const useInitialize = venueId => {
  const logger = useMemo(() => {
    return getLogger('use-contentstack');
  }, []);

  const configUrls = useSelector(({ config }: ReturnType<IStoreState>) => config.URLS?.contentDelivery);
  const dispatch = useDispatch<(action: SetDataAction) => void>();

  const client = useApolloClient();

  useEffect(() => {
    const requestData = async () => {
      try {
        const response = await client.query({
          query: contentstackQuery,
          variables: { venueId },
          fetchPolicy: 'no-cache'
        });

        const data = addBaseUrlToNewStackData({ baseUrl: configUrls.base, data: response.data.getVenueData });
        dispatch({
          type: 'setContentstackData',
          data
        });
      } catch (error) {
        logger.error(error, 'Failed to fetch contentstack data v2');
      }
    };

    if (!configUrls) {
      return;
    }

    requestData();
    const interval = setInterval(requestData, 900000); // 15 minutes
    return () => clearInterval(interval);
  }, [configUrls, client, dispatch, venueId, logger]);
};

export const addBaseUrlToNewValue = (baseUrl: string, value?: string) => {
  if (!value) return value;
  const domainAndPathsToRemove = [
    'https://images.contentstack.io/v3/assets/',
    'https://assets.contentstack.io/v3/assets/'
  ];
  const withoutDomain = domainAndPathsToRemove.reduce((acc, path) => {
    return acc.replace(path, '');
  }, value);
  const withNewDomain = `${baseUrl}${withoutDomain}`;
  return withNewDomain;
};

const addBaseUrlToNewStackData = ({
  baseUrl,
  data
}: {
  baseUrl: string;
  data: NewContentstackState;
}): NewContentstackState => {
  const urlKeys = [
    'other.ticket_logo.url',
    'other.contests_promo.url',
    'other.temp_pos_secondary.url',
    'other.sheets_footer.url',
    'other.prize_draw_rules.url',
    'regulatory.house_rules.url',

    'rewards_card.no_rewards_card_bet_success_modal_footer_image.url',
    'rewards_card.no_rewards_card_right_panel.url',
    'rewards_card.rewards_card_right_panel.url',
    'rewards_card.no_rewards_card_right_panel_home.url',
    'rewards_card.rewards_card_right_panel_home.url',
    'rewards_card.no_rewards_card_bet_slip_image.url',

    'home_page.image.url',
    'home_page.temporary_image.url',
    'home_page.rg_image.url',

    'mobile_registration.success_background.url',
    'mobile_registration.sidebar_button.url',
    'mobile_registration.terms_and_conditions_html.url',
    'mobile_registration.success_qr_code.url',
    'mobile_registration.promotion_terms_and_conditions_html.url',

    'homepage_merchandising.image.url',
    'homepage_merchandising.temporary_image.url'
  ];

  const urlArrayKeys = [
    'mobile_registration.terms_and_conditions_pdfs',
    'mobile_registration.promotion_terms_and_conditions_pdfs'
  ];

  const clonedData = lodash.cloneDeep(data);

  urlKeys.forEach(key => {
    const value = lodash.get(clonedData, key);
    lodash.set(clonedData, key, addBaseUrlToNewValue(baseUrl, value));
  });

  urlArrayKeys.forEach(key => {
    const value = lodash.get(clonedData, key);
    if (!Array.isArray(value)) return;
    const newValue = value.map(item => {
      if (item?.url) {
        return {
          ...item,
          url: addBaseUrlToNewValue(baseUrl, item.url)
        };
      }
      return item;
    });

    lodash.set(clonedData, key, newValue);
  });

  return clonedData;
};

/**
 * Old code path
 * @deprecated
 */
export const addBaseUrlToData = ({
  baseUrl,
  data
}: {
  baseUrl: string;
  data: ContentStackState;
}): ContentStackState => {
  return {
    customer_disputes: data?.customer_disputes,
    responsible_gaming_html: data?.responsible_gaming_html,
    house_rules: addBaseUrl(data?.house_rules),
    no_rewards_card_right_panel: addBaseUrl(data?.no_rewards_card_right_panel),
    rewards_card_right_panel: addBaseUrl(data?.rewards_card_right_panel),
    no_rewards_card_right_panel_home: addBaseUrl(data?.no_rewards_card_right_panel_home),
    rewards_card_right_panel_home: addBaseUrl(data?.rewards_card_right_panel_home),
    no_rewards_card_bet_success_modal_footer_image: addBaseUrl(data?.no_rewards_card_bet_success_modal_footer_image),
    no_rewards_card_bet_slip_image: addBaseUrl(data?.no_rewards_card_bet_slip_image),
    home_page_image: addBaseUrl(data?.home_page_image),
    home_page_temporary_cta_link: data?.home_page_temporary_cta_link,
    home_page_rg_image: addBaseUrl(data?.home_page_rg_image),
    home_page_temporary_image: addBaseUrl(data?.home_page_temporary_image),
    home_page_temporary_image_start: data?.home_page_temporary_image_start,
    home_page_temporary_image_expiry: data?.home_page_temporary_image_expiry,
    merch_home_page_image: addBaseUrl(data?.merch_home_page_image),
    merch_home_page_temporary_cta_link: data?.merch_home_page_temporary_cta_link,
    merch_home_page_temporary_image: addBaseUrl(data?.merch_home_page_temporary_image),
    merch_home_page_temporary_image_start: data?.merch_home_page_temporary_image_start,
    merch_home_page_temporary_image_expiry: data?.merch_home_page_temporary_image_expiry,
    mobile_registration_promotion_terms_and_conditions_html: addBaseUrl(
      data?.mobile_registration_promotion_terms_and_conditions_html
    ),
    mobile_registration_promotion_terms_and_conditions_links:
      data?.mobile_registration_promotion_terms_and_conditions_links,
    mobile_registration_promotion_terms_and_conditions_pdfs: addBaseUrlToArray(
      data?.mobile_registration_promotion_terms_and_conditions_pdfs
    ),
    mobile_registration_sidebar_button: addBaseUrl(data?.mobile_registration_sidebar_button),
    mobile_registration_success_background: addBaseUrl(data?.mobile_registration_success_background),
    mobile_registration_success_qr_code: addBaseUrl(data?.mobile_registration_success_qr_code),
    mobile_registration_terms_and_conditions_html: addBaseUrl(data?.mobile_registration_terms_and_conditions_html),
    mobile_registration_terms_and_conditions_links: data?.mobile_registration_terms_and_conditions_links,
    mobile_registration_terms_and_conditions_pdfs: addBaseUrlToArray(
      data?.mobile_registration_terms_and_conditions_pdfs
    ),
    temp_pos_secondary: addBaseUrl(data?.temp_pos_secondary),
    ticket_logo: addBaseUrl(data?.ticket_logo)
  };

  function addBaseUrlToArray(value?: string[]) {
    return value && value.map(element => `${baseUrl}${element}`);
  }

  function addBaseUrl(value?: string) {
    return value && `${baseUrl}${value}`;
  }
};

export default useContentStack;

export const contentstackQuery = gql`
  query getVenueData($venueId: String!) {
    getVenueData(venueId: $venueId) {
      other {
        ticket_logo {
          url
        }
        contests_promo {
          url
        }
        temp_pos_secondary {
          url
        }
        sheets_footer {
          url
        }
        prize_draw_rules {
          url
        }
      }
      regulatory {
        customer_disputes
        responsible_gaming_html
        house_rules {
          url
        }
      }

      rewards_card {
        no_rewards_card_bet_slip_image {
          url
        }
        no_rewards_card_bet_success_modal_footer_image {
          url
        }
        no_rewards_card_right_panel {
          url
        }
        no_rewards_card_right_panel_home {
          url
        }
        rewards_card_right_panel {
          url
        }
        rewards_card_right_panel_home {
          url
        }
      }

      locale
      title

      home_page {
        image {
          url
        }
        temporary_image_expiry
        temporary_image {
          url
        }
        temporary_image_start
        temporary_cta_link
        rg_image {
          url
        }
      }

      top_screen {
        override_top_screen_images
        top_screen_images {
          navigation_link
          image {
            url
          }
        }
      }

      state {
        uid
      }
      state_id
      brand {
        uid
      }
      venue_id

      mobile_registration {
        promotion_terms_and_conditions_links
        success_background {
          url
        }
        sidebar_button {
          url
        }
        terms_and_conditions_pdfs {
          url
        }
        promotion_terms_and_conditions_pdfs {
          url
        }
        terms_and_conditions_html {
          url
        }
        success_qr_code {
          url
        }
        terms_and_conditions_links
        promotion_terms_and_conditions_html {
          url
        }
      }

      abbreviation
      brand_id

      homepage_merchandising {
        image {
          url
        }
        temporary_cta_link
        temporary_image {
          url
        }
        temporary_image_start
        temporary_image_expiry
      }
    }
  }
`;
